/**
 * ItemRegistry - Central registry for ALL items (blocks + non-block items)
 *
 * Item ID conventions:
 *   Positive IDs (1-60) = placeable blocks (from BlockId enum)
 *   Negative IDs = non-block items (tools, materials, food, etc.)
 *
 * This file is dependency-free (does not import other game modules)
 * so it can be safely imported from anywhere.
 */

// ============================================================
// ITEM ID ENUM
// ============================================================

export const ItemId = {
  // Existing non-block items (keep backward compatibility)
  STICK:           -1,
  CRAFTING_TABLE:  -2,
  FURNACE:         -3,

  // Raw ores / gems (mined drops)
  RAW_IRON:        -10,
  RAW_GOLD:        -11,
  DIAMOND_GEM:     -12,
  COAL:            -13,
  REDSTONE_DUST:   -14,
  LAPIS:           -15,
  EMERALD_GEM:     -16,

  // Miscellaneous materials
  FLINT:           -17,
  STRING:          -18,
  BONE:            -19,
  ARROW_ITEM:      -20,
  GUNPOWDER:       -21,

  // Hostile mob drops
  ROTTEN_FLESH:    -22,
  SPIDER_EYE:      -23,

  // Passive mob drops
  FEATHER:         -24,
  LEATHER:         -25,

  // Raw meats
  RAW_BEEF:        -26,
  COOKED_BEEF:     -27,
  RAW_PORK:        -28,
  COOKED_PORK:     -29,
  RAW_CHICKEN:     -30,
  COOKED_CHICKEN:  -31,

  // Other food
  BREAD:           -32,
  APPLE:           -33,

  // Smelted ingots
  IRON_INGOT:      -34,
  GOLD_INGOT:      -35,

  // Farming items
  WHEAT_ITEM:      -36,
  SEEDS:           -37,
  BONE_MEAL:       -38,

  // Bow & arrow
  BOW:             -39,

  // New mob drops
  SLIME_BALL:      -40,
  ENDER_PEARL:     -41,

  // Potions
  POTION_SPEED:           -42,
  POTION_STRENGTH:        -43,
  POTION_HEALING:         -44,
  POTION_FIRE_RESISTANCE: -45,
  POTION_NIGHT_VISION:    -46,
  POTION_REGENERATION:    -47,

  // Brewing materials
  GLASS_BOTTLE:    -48,
  NETHER_WART:     -49,
  BLAZE_POWDER:    -50,
  AWKWARD_POTION:  -51,

  // Nether mob drops
  GHAST_TEAR:      -52,
  BLAZE_ROD:       -53,
  NETHER_QUARTZ:   -54,

  // Armor - Iron
  IRON_HELMET:     -55,
  IRON_CHESTPLATE: -56,
  IRON_LEGGINGS:   -57,
  IRON_BOOTS:      -58,

  // Armor - Gold
  GOLD_HELMET:     -68,
  GOLD_CHESTPLATE: -69,
  GOLD_LEGGINGS:   -70,
  GOLD_BOOTS:      -71,

  // Armor - Diamond
  DIAMOND_HELMET:     -72,
  DIAMOND_CHESTPLATE: -73,
  DIAMOND_LEGGINGS:   -74,
  DIAMOND_BOOTS:      -75,

  // Utility & decorative
  BUCKET:          -59,
  PAINTING:        -60,
  BOOK:            -61,
  MINECART:        -62,
  RAILS:           -63,
  PISTON:          -64,

  // Crafting ingredients
  GLOWSTONE_DUST:  -65,
  SNOWBALL:        -66,
  BRICK_ITEM:      -67,

  // Map & paper
  PAPER:           -76,
  MAP:             -77,

  // Fishing
  FISHING_ROD:     -78,
  RAW_COD:         -79,
  COOKED_COD:      -80,
  RAW_SALMON:      -81,
  COOKED_SALMON:   -82,
  TROPICAL_FISH:   -83,
  PUFFERFISH:      -84,

  // Boss drops
  NETHER_STAR:     -85,
  WITHER_SKULL:    -86,

  // End items
  ENDER_EYE:       -87,
  DRAGON_BREATH:   -88,
  ELYTRA:          -89,
  SHULKER_SHELL:   -90,
  BLAZE_POWDER:    -91,

  // Farming & Village items (Fase 5)
  CARROT_ITEM:     -92,
  POTATO_ITEM:     -93,
  BEETROOT_ITEM:   -94,
  BAKED_POTATO:    -95,
  BEETROOT_SOUP:   -96,
  SWEET_BERRIES:   -97,
  SUGAR:           -98,
  PUMPKIN_SEEDS:   -99,
  MELON_SEEDS:     -100,
  MELON_SLICE:     -101,
  TRIDENT:         -102,

  // Exploration & Adventure items (Fase 7)
  SHIELD:          -103,
  CROSSBOW:        -104,
  HEART_OF_SEA:    -105,
  TREASURE_MAP:    -106,

  // Tecnología (Fase 17)
  PHONE:           -107,
  COMPUTER:        -108,
};

// ============================================================
// ITEM NAMES (Spanish)
// ============================================================

const ITEM_NAMES = {
  [ItemId.STICK]:          'Palo',
  [ItemId.CRAFTING_TABLE]: 'Mesa de Crafteo',
  [ItemId.FURNACE]:        'Horno',

  [ItemId.RAW_IRON]:       'Hierro Crudo',
  [ItemId.RAW_GOLD]:       'Oro Crudo',
  [ItemId.DIAMOND_GEM]:    'Diamante',
  [ItemId.COAL]:           'Carbón',
  [ItemId.REDSTONE_DUST]:  'Polvo de Redstone',
  [ItemId.LAPIS]:          'Lapislázuli',
  [ItemId.EMERALD_GEM]:    'Esmeralda',

  [ItemId.FLINT]:          'Pedernal',
  [ItemId.STRING]:         'Hilo',
  [ItemId.BONE]:           'Hueso',
  [ItemId.ARROW_ITEM]:     'Flecha',
  [ItemId.GUNPOWDER]:      'Pólvora',

  [ItemId.ROTTEN_FLESH]:   'Carne Podrida',
  [ItemId.SPIDER_EYE]:     'Ojo de Araña',

  [ItemId.FEATHER]:        'Pluma',
  [ItemId.LEATHER]:        'Cuero',

  [ItemId.RAW_BEEF]:       'Carne Cruda',
  [ItemId.COOKED_BEEF]:    'Filete',
  [ItemId.RAW_PORK]:       'Cerdo Crudo',
  [ItemId.COOKED_PORK]:    'Cerdo Cocido',
  [ItemId.RAW_CHICKEN]:    'Pollo Crudo',
  [ItemId.COOKED_CHICKEN]: 'Pollo Cocido',

  [ItemId.BREAD]:          'Pan',
  [ItemId.APPLE]:          'Manzana',

  [ItemId.IRON_INGOT]:     'Lingote de Hierro',
  [ItemId.GOLD_INGOT]:     'Lingote de Oro',

  [ItemId.WHEAT_ITEM]:     'Trigo',
  [ItemId.SEEDS]:          'Semillas',
  [ItemId.BONE_MEAL]:      'Harina de Hueso',

  [ItemId.BOW]:            'Arco',

  [ItemId.SLIME_BALL]:     'Bola de Slime',
  [ItemId.ENDER_PEARL]:    'Perla de Ender',

  [ItemId.POTION_SPEED]:           'Poción de Velocidad',
  [ItemId.POTION_STRENGTH]:        'Poción de Fuerza',
  [ItemId.POTION_HEALING]:         'Poción de Curación',
  [ItemId.POTION_FIRE_RESISTANCE]: 'Poción de Resist. Fuego',
  [ItemId.POTION_NIGHT_VISION]:    'Poción de Visión Nocturna',
  [ItemId.POTION_REGENERATION]:    'Poción de Regeneración',

  [ItemId.GLASS_BOTTLE]:   'Botella de Vidrio',
  [ItemId.NETHER_WART]:    'Verruga del Nether',
  [ItemId.BLAZE_POWDER]:   'Polvo de Blaze',
  [ItemId.AWKWARD_POTION]:  'Poción Rara',

  [ItemId.GHAST_TEAR]:     'Lágrima de Ghast',
  [ItemId.BLAZE_ROD]:      'Vara de Blaze',
  [ItemId.NETHER_QUARTZ]:  'Cuarzo del Nether',

  [ItemId.IRON_HELMET]:     'Casco de Hierro',
  [ItemId.IRON_CHESTPLATE]: 'Pechera de Hierro',
  [ItemId.IRON_LEGGINGS]:   'Pantalones de Hierro',
  [ItemId.IRON_BOOTS]:      'Botas de Hierro',

  [ItemId.GOLD_HELMET]:     'Casco de Oro',
  [ItemId.GOLD_CHESTPLATE]: 'Pechera de Oro',
  [ItemId.GOLD_LEGGINGS]:   'Pantalones de Oro',
  [ItemId.GOLD_BOOTS]:      'Botas de Oro',

  [ItemId.DIAMOND_HELMET]:     'Casco de Diamante',
  [ItemId.DIAMOND_CHESTPLATE]: 'Pechera de Diamante',
  [ItemId.DIAMOND_LEGGINGS]:   'Pantalones de Diamante',
  [ItemId.DIAMOND_BOOTS]:      'Botas de Diamante',

  [ItemId.BUCKET]:          'Balde',
  [ItemId.PAINTING]:        'Cuadro',
  [ItemId.BOOK]:            'Libro',
  [ItemId.MINECART]:        'Vagoneta',
  [ItemId.RAILS]:           'Rieles',
  [ItemId.PISTON]:          'Pistón',

  [ItemId.GLOWSTONE_DUST]:  'Polvo de Piedra Luminosa',
  [ItemId.SNOWBALL]:         'Bola de Nieve',
  [ItemId.BRICK_ITEM]:       'Ladrillo',

  [ItemId.PAPER]:            'Papel',
  [ItemId.MAP]:              'Mapa',

  // Fishing
  [ItemId.FISHING_ROD]:      'Caña de Pescar',
  [ItemId.RAW_COD]:          'Bacalao Crudo',
  [ItemId.COOKED_COD]:       'Bacalao Cocido',
  [ItemId.RAW_SALMON]:       'Salmón Crudo',
  [ItemId.COOKED_SALMON]:    'Salmón Cocido',
  [ItemId.TROPICAL_FISH]:    'Pez Tropical',
  [ItemId.PUFFERFISH]:       'Pez Globo',

  // Boss drops
  [ItemId.NETHER_STAR]:      'Estrella del Nether',
  [ItemId.WITHER_SKULL]:     'Cráneo de Wither',

  // End items
  [ItemId.ENDER_EYE]:        'Ojo de Ender',
  [ItemId.DRAGON_BREATH]:    'Aliento de Dragón',
  [ItemId.ELYTRA]:           'Élitros',
  [ItemId.SHULKER_SHELL]:    'Caparazón de Shulker',
  [ItemId.BLAZE_POWDER]:     'Polvo de Blaze',

  // Farming & Village items (Fase 5)
  [ItemId.CARROT_ITEM]:      'Zanahoria',
  [ItemId.POTATO_ITEM]:      'Papa',
  [ItemId.BEETROOT_ITEM]:    'Remolacha',
  [ItemId.BAKED_POTATO]:     'Papa al Horno',
  [ItemId.BEETROOT_SOUP]:    'Sopa de Remolacha',
  [ItemId.SWEET_BERRIES]:    'Bayas Dulces',
  [ItemId.SUGAR]:            'Azúcar',
  [ItemId.PUMPKIN_SEEDS]:    'Semillas de Calabaza',
  [ItemId.MELON_SEEDS]:      'Semillas de Melón',
  [ItemId.MELON_SLICE]:      'Rebanada de Melón',
  [ItemId.TRIDENT]:          'Tridente',

  // Exploration & Adventure items (Fase 7)
  [ItemId.SHIELD]:           'Escudo',
  [ItemId.CROSSBOW]:         'Ballesta',
  [ItemId.HEART_OF_SEA]:     'Corazón del Mar',
  [ItemId.TREASURE_MAP]:     'Mapa del Tesoro',

  // Tecnología (Fase 17)
  [ItemId.PHONE]:            'Teléfono',
  [ItemId.COMPUTER]:         'Computador',
};

// ============================================================
// ITEM COLORS (for hotbar / inventory rendering)
// ============================================================

const ITEM_COLORS = {
  [ItemId.STICK]:          '#B89050',
  [ItemId.CRAFTING_TABLE]: '#8B6B3E',
  [ItemId.FURNACE]:        '#7A7A7A',

  [ItemId.RAW_IRON]:       '#C4A06A',
  [ItemId.RAW_GOLD]:       '#E8D44A',
  [ItemId.DIAMOND_GEM]:    '#5AE8D4',
  [ItemId.COAL]:           '#333333',
  [ItemId.REDSTONE_DUST]:  '#DD2222',
  [ItemId.LAPIS]:          '#2244AA',
  [ItemId.EMERALD_GEM]:    '#22CC44',

  [ItemId.FLINT]:          '#555555',
  [ItemId.STRING]:         '#E0E0E0',
  [ItemId.BONE]:           '#E8E8D8',
  [ItemId.ARROW_ITEM]:     '#8B6B3E',
  [ItemId.GUNPOWDER]:      '#666666',

  [ItemId.ROTTEN_FLESH]:   '#8B5A2B',
  [ItemId.SPIDER_EYE]:     '#993333',

  [ItemId.FEATHER]:        '#F0F0F0',
  [ItemId.LEATHER]:        '#A0622A',

  [ItemId.RAW_BEEF]:       '#CC4444',
  [ItemId.COOKED_BEEF]:    '#884422',
  [ItemId.RAW_PORK]:       '#DDAAAA',
  [ItemId.COOKED_PORK]:    '#AA6633',
  [ItemId.RAW_CHICKEN]:    '#EEBBAA',
  [ItemId.COOKED_CHICKEN]: '#BB7744',

  [ItemId.BREAD]:          '#C49840',
  [ItemId.APPLE]:          '#DD2222',

  [ItemId.IRON_INGOT]:     '#D8D8D8',
  [ItemId.GOLD_INGOT]:     '#FFD700',

  [ItemId.WHEAT_ITEM]:     '#D4AA30',
  [ItemId.SEEDS]:          '#6A8A20',
  [ItemId.BONE_MEAL]:      '#E8E8D0',

  [ItemId.BOW]:            '#6B4226',

  [ItemId.SLIME_BALL]:     '#66CC44',
  [ItemId.ENDER_PEARL]:    '#116655',

  [ItemId.POTION_SPEED]:           '#7CAFC6',
  [ItemId.POTION_STRENGTH]:        '#932423',
  [ItemId.POTION_HEALING]:         '#F82423',
  [ItemId.POTION_FIRE_RESISTANCE]: '#E49A3A',
  [ItemId.POTION_NIGHT_VISION]:    '#1F1FA1',
  [ItemId.POTION_REGENERATION]:    '#CD5CAB',

  [ItemId.GLASS_BOTTLE]:   '#AADDEE',
  [ItemId.NETHER_WART]:    '#8B2020',
  [ItemId.BLAZE_POWDER]:   '#FFCC00',
  [ItemId.AWKWARD_POTION]:  '#6A4A8A',

  [ItemId.GHAST_TEAR]:     '#E8E8FF',
  [ItemId.BLAZE_ROD]:      '#FFB840',
  [ItemId.NETHER_QUARTZ]:  '#F0E8E0',

  [ItemId.IRON_HELMET]:     '#C8C8C8',
  [ItemId.IRON_CHESTPLATE]: '#C8C8C8',
  [ItemId.IRON_LEGGINGS]:   '#C8C8C8',
  [ItemId.IRON_BOOTS]:      '#C8C8C8',

  [ItemId.GOLD_HELMET]:     '#FFD700',
  [ItemId.GOLD_CHESTPLATE]: '#FFD700',
  [ItemId.GOLD_LEGGINGS]:   '#FFD700',
  [ItemId.GOLD_BOOTS]:      '#FFD700',

  [ItemId.DIAMOND_HELMET]:     '#4AEDD9',
  [ItemId.DIAMOND_CHESTPLATE]: '#4AEDD9',
  [ItemId.DIAMOND_LEGGINGS]:   '#4AEDD9',
  [ItemId.DIAMOND_BOOTS]:      '#4AEDD9',

  [ItemId.BUCKET]:          '#AAAAAA',
  [ItemId.PAINTING]:        '#8B5E3C',
  [ItemId.BOOK]:            '#8B4513',
  [ItemId.MINECART]:        '#999999',
  [ItemId.RAILS]:           '#B0B0B0',
  [ItemId.PISTON]:          '#7A7A5A',

  [ItemId.GLOWSTONE_DUST]:  '#DDCC40',
  [ItemId.SNOWBALL]:         '#F0F0FF',
  [ItemId.BRICK_ITEM]:       '#AA5533',

  [ItemId.PAPER]:            '#F5F5DC',
  [ItemId.MAP]:              '#D2B48C',

  // Fishing
  [ItemId.FISHING_ROD]:      '#8B4513',
  [ItemId.RAW_COD]:          '#C4A882',
  [ItemId.COOKED_COD]:       '#D4A862',
  [ItemId.RAW_SALMON]:       '#FF6347',
  [ItemId.COOKED_SALMON]:    '#FF8C69',
  [ItemId.TROPICAL_FISH]:    '#FFD700',
  [ItemId.PUFFERFISH]:       '#FFE4B5',

  // Boss drops
  [ItemId.NETHER_STAR]:      '#FFFFAA',
  [ItemId.WITHER_SKULL]:     '#333333',

  // End items
  [ItemId.ENDER_EYE]:        '#0EE8A0',
  [ItemId.DRAGON_BREATH]:    '#8E44AD',
  [ItemId.ELYTRA]:           '#9B9B9B',
  [ItemId.SHULKER_SHELL]:    '#9B59B6',
  [ItemId.BLAZE_POWDER]:     '#FFD700',

  // Farming & Village items (Fase 5)
  [ItemId.CARROT_ITEM]:      '#E87020',
  [ItemId.POTATO_ITEM]:      '#C4A060',
  [ItemId.BEETROOT_ITEM]:    '#8B1A1A',
  [ItemId.BAKED_POTATO]:     '#D4AA40',
  [ItemId.BEETROOT_SOUP]:    '#AA3030',
  [ItemId.SWEET_BERRIES]:    '#DD3333',
  [ItemId.SUGAR]:            '#F0F0F0',
  [ItemId.PUMPKIN_SEEDS]:    '#6A8A20',
  [ItemId.MELON_SEEDS]:      '#5A9A30',
  [ItemId.MELON_SLICE]:      '#6AAA30',
  [ItemId.TRIDENT]:          '#3388BB',

  // Exploration & Adventure items (Fase 7)
  [ItemId.SHIELD]:           '#8B6B3E',
  [ItemId.CROSSBOW]:         '#6B4226',
  [ItemId.HEART_OF_SEA]:     '#1A8AAA',
  [ItemId.TREASURE_MAP]:     '#C4A060',

  // Tecnología (Fase 17)
  [ItemId.PHONE]:            '#1A1A1C',
  [ItemId.COMPUTER]:         '#9B9B9B',
};

// ============================================================
// Block IDs referenced locally (avoid importing BlockType)
// Keep in sync with BlockType.js -- only the ones needed for
// drop rules and mob drops.
// ============================================================

const _B = {
  AIR: 0,
  GRASS: 1,
  DIRT: 2,
  STONE: 3,
  SAND: 4,
  WOOD: 5,
  LEAVES: 6,
  WATER: 7,
  BEDROCK: 8,
  COAL_ORE: 18,
  IRON_ORE: 19,
  GOLD_ORE: 20,
  DIAMOND_ORE: 21,
  REDSTONE_ORE: 22,
  LAPIS_ORE: 23,
  EMERALD_ORE: 24,
  WHEAT: 15,
  TALL_GRASS: 43,
  GRAVEL: 46,
  WOOL_WHITE: 54,
  DOOR_OPEN: 17,
  FARMLAND: 62,
  FARMLAND_WET: 63,
};

// ============================================================
// PUBLIC API
// ============================================================

/**
 * Get the Spanish display name for any item ID (positive = block, negative = item).
 * For block IDs, returns null (the caller should fall back to BLOCK_NAMES).
 */
export function getItemName(id) {
  if (id > 0) return null; // Block -- let caller use BLOCK_NAMES
  return ITEM_NAMES[id] || 'Objeto';
}

/**
 * Get the color for any non-block item ID.
 * For block IDs, returns null (caller should use BLOCK_COLORS).
 */
export function getItemColor(id) {
  if (id > 0) return null;
  return ITEM_COLORS[id] || '#888888';
}

/**
 * Check if an item ID represents an edible item.
 */
export function isEdible(id) {
  return FOOD_VALUES[id] !== undefined;
}

/**
 * Get food stats for an edible item.
 * @returns {{ hunger: number, saturation: number, poisonChance?: number }} or null
 */
export function getFoodValue(id) {
  return FOOD_VALUES[id] || null;
}

// ============================================================
// ARMOR VALUES
// ============================================================

const ARMOR_VALUES = {
  // Iron armor
  [ItemId.IRON_HELMET]:     { slot: 'head',  armorSlot: 'helmet',     material: 'iron',    defense: 2 },
  [ItemId.IRON_CHESTPLATE]: { slot: 'chest', armorSlot: 'chestplate', material: 'iron',    defense: 6 },
  [ItemId.IRON_LEGGINGS]:   { slot: 'legs',  armorSlot: 'leggings',   material: 'iron',    defense: 5 },
  [ItemId.IRON_BOOTS]:      { slot: 'feet',  armorSlot: 'boots',      material: 'iron',    defense: 2 },

  // Gold armor
  [ItemId.GOLD_HELMET]:     { slot: 'head',  armorSlot: 'helmet',     material: 'gold',    defense: 2 },
  [ItemId.GOLD_CHESTPLATE]: { slot: 'chest', armorSlot: 'chestplate', material: 'gold',    defense: 5 },
  [ItemId.GOLD_LEGGINGS]:   { slot: 'legs',  armorSlot: 'leggings',   material: 'gold',    defense: 3 },
  [ItemId.GOLD_BOOTS]:      { slot: 'feet',  armorSlot: 'boots',      material: 'gold',    defense: 1 },

  // Diamond armor
  [ItemId.DIAMOND_HELMET]:     { slot: 'head',  armorSlot: 'helmet',     material: 'diamond', defense: 3 },
  [ItemId.DIAMOND_CHESTPLATE]: { slot: 'chest', armorSlot: 'chestplate', material: 'diamond', defense: 8 },
  [ItemId.DIAMOND_LEGGINGS]:   { slot: 'legs',  armorSlot: 'leggings',   material: 'diamond', defense: 6 },
  [ItemId.DIAMOND_BOOTS]:      { slot: 'feet',  armorSlot: 'boots',      material: 'diamond', defense: 3 },
};

/**
 * Check if an item ID represents an armor piece.
 */
export function isArmor(id) {
  return ARMOR_VALUES[id] !== undefined;
}

/**
 * Get armor stats for an armor item.
 * @returns {{ slot: string, defense: number }} or null
 */
export function getArmorValue(id) {
  return ARMOR_VALUES[id] || null;
}

// ============================================================
// FOOD VALUES
// ============================================================

const FOOD_VALUES = {
  [ItemId.ROTTEN_FLESH]:   { hunger: 4,  saturation: 0.8,  poisonChance: 0.8 },
  [ItemId.RAW_BEEF]:       { hunger: 3,  saturation: 1.8 },
  [ItemId.COOKED_BEEF]:    { hunger: 8,  saturation: 12.8 },
  [ItemId.RAW_PORK]:       { hunger: 3,  saturation: 1.8 },
  [ItemId.COOKED_PORK]:    { hunger: 8,  saturation: 12.8 },
  [ItemId.RAW_CHICKEN]:    { hunger: 2,  saturation: 1.2 },
  [ItemId.COOKED_CHICKEN]: { hunger: 6,  saturation: 7.2 },
  [ItemId.BREAD]:          { hunger: 5,  saturation: 6.0 },
  [ItemId.APPLE]:          { hunger: 4,  saturation: 2.4 },

  // Potions (drinkable -- hunger: 0, potionEffect triggers the effect)
  [ItemId.POTION_SPEED]:           { hunger: 0, saturation: 0, potionEffect: 'SPEED' },
  [ItemId.POTION_STRENGTH]:        { hunger: 0, saturation: 0, potionEffect: 'STRENGTH' },
  [ItemId.POTION_HEALING]:         { hunger: 0, saturation: 0, potionEffect: 'HEALING' },
  [ItemId.POTION_FIRE_RESISTANCE]: { hunger: 0, saturation: 0, potionEffect: 'FIRE_RESISTANCE' },
  [ItemId.POTION_NIGHT_VISION]:    { hunger: 0, saturation: 0, potionEffect: 'NIGHT_VISION' },
  [ItemId.POTION_REGENERATION]:    { hunger: 0, saturation: 0, potionEffect: 'REGENERATION' },

  // Fish
  [ItemId.RAW_COD]:          { hunger: 2,  saturation: 0.4 },
  [ItemId.COOKED_COD]:       { hunger: 5,  saturation: 6.0 },
  [ItemId.RAW_SALMON]:       { hunger: 2,  saturation: 0.4 },
  [ItemId.COOKED_SALMON]:    { hunger: 6,  saturation: 9.6 },
  [ItemId.TROPICAL_FISH]:    { hunger: 1,  saturation: 0.2 },
  [ItemId.PUFFERFISH]:       { hunger: 1,  saturation: 0.2, poisonChance: 1.0 },

  // Farming crops (Fase 5)
  [ItemId.CARROT_ITEM]:      { hunger: 3,  saturation: 3.6 },
  [ItemId.POTATO_ITEM]:      { hunger: 1,  saturation: 0.6 },
  [ItemId.BAKED_POTATO]:     { hunger: 5,  saturation: 6.0 },
  [ItemId.BEETROOT_ITEM]:    { hunger: 1,  saturation: 1.2 },
  [ItemId.SWEET_BERRIES]:    { hunger: 2,  saturation: 0.4 },
  [ItemId.MELON_SLICE]:      { hunger: 2,  saturation: 1.2 },
};

// ============================================================
// BLOCK DROP RULES
// ============================================================

/**
 * Determine what item drops when a block is mined.
 *
 * @param {number} blockId - The block that was broken
 * @param {number|null} toolId - Currently unused; reserved for silk-touch / fortune logic
 * @returns {{ id: number, count: number, isBlock: boolean } | null}
 *   id     = BlockId (positive) or ItemId (negative)
 *   count  = how many to drop
 *   isBlock = true if id is a block that should be placed via dropItem(blockId, ...)
 *
 *   Returns null when nothing should drop (e.g. leaves with no apple).
 */
export function getBlockDrop(blockId, toolId) {
  switch (blockId) {
    // --- Ores drop their material ---
    case _B.COAL_ORE:
      return { id: ItemId.COAL, count: 1, isBlock: false };

    case _B.IRON_ORE:
      return { id: ItemId.RAW_IRON, count: 1, isBlock: false };

    case _B.GOLD_ORE:
      return { id: ItemId.RAW_GOLD, count: 1, isBlock: false };

    case _B.DIAMOND_ORE:
      return { id: ItemId.DIAMOND_GEM, count: 1, isBlock: false };

    case _B.REDSTONE_ORE:
      return { id: ItemId.REDSTONE_DUST, count: 4 + Math.floor(Math.random() * 2), isBlock: false };

    case _B.LAPIS_ORE:
      return { id: ItemId.LAPIS, count: 4 + Math.floor(Math.random() * 5), isBlock: false };

    case _B.EMERALD_ORE:
      return { id: ItemId.EMERALD_GEM, count: 1, isBlock: false };

    // --- Gravel: 10% chance to drop flint ---
    case _B.GRAVEL:
      if (Math.random() < 0.10) {
        return { id: ItemId.FLINT, count: 1, isBlock: false };
      }
      return { id: _B.GRAVEL, count: 1, isBlock: true };

    // --- Leaves: 2% chance to drop apple, otherwise nothing ---
    case _B.LEAVES:
      if (Math.random() < 0.02) {
        return { id: ItemId.APPLE, count: 1, isBlock: false };
      }
      return null;

    // --- Tall grass: 30% chance to drop seeds ---
    case _B.TALL_GRASS:
      if (Math.random() < 0.30) {
        return { id: ItemId.SEEDS, count: 1, isBlock: false };
      }
      return null;

    // --- Wheat: always drops wheat item + 0-3 seeds ---
    case _B.WHEAT: {
      // Return the wheat item; the caller should also call getWheatBonusSeeds()
      return { id: ItemId.WHEAT_ITEM, count: 1, isBlock: false };
    }

    // --- Redstone wire drops redstone dust ---
    case _B.REDSTONE_WIRE:
      return { id: ItemId.REDSTONE_DUST, count: 1, isBlock: false };

    // --- Farmland drops dirt ---
    case _B.FARMLAND:
    case _B.FARMLAND_WET:
      return { id: _B.DIRT, count: 1, isBlock: true };

    // --- Default: block drops itself ---
    default:
      // Skip AIR, WATER, BEDROCK
      if (blockId === _B.AIR || blockId === _B.WATER || blockId === _B.BEDROCK) {
        return null;
      }
      // Normalize open doors to closed form
      if (blockId === _B.DOOR_OPEN) blockId = 16; // DOOR_CLOSED
      return { id: blockId, count: 1, isBlock: true };
  }
}

/**
 * Get bonus seed drops for wheat (0-3 seeds). Call alongside getBlockDrop for wheat.
 * @returns {{ id: number, count: number, isBlock: boolean } | null}
 */
export function getWheatBonusSeeds() {
  const seedCount = Math.floor(Math.random() * 4); // 0-3
  if (seedCount > 0) {
    return { id: ItemId.SEEDS, count: seedCount, isBlock: false };
  }
  return null;
}

// ============================================================
// MOB DROP RULES  (data-driven loot tables)
// ============================================================

/**
 * Loot table format per entry:
 *   itemId  - ItemId (negative) for non-block items
 *   blockId - _B block id (positive) for block drops (e.g. wool)
 *   min     - minimum drop count (inclusive)
 *   max     - maximum drop count (inclusive)
 *   chance  - probability 0-1 that this entry drops at all
 *   isBlock - true when the drop is a placeable block
 *
 * Exactly one of itemId or blockId must be set per entry.
 */
const MOB_DROPS = {
  // --- Passive mobs ---
  PIG:     [{ itemId: ItemId.RAW_PORK,     min: 1, max: 3, chance: 1.0,   isBlock: false }],
  COW:     [{ itemId: ItemId.RAW_BEEF,     min: 1, max: 3, chance: 1.0,   isBlock: false },
            { itemId: ItemId.LEATHER,       min: 0, max: 2, chance: 1.0,   isBlock: false }],
  SHEEP:   [{ blockId: _B.WOOL_WHITE,      min: 1, max: 1, chance: 1.0,   isBlock: true  }],
  CHICKEN: [{ itemId: ItemId.RAW_CHICKEN,  min: 1, max: 1, chance: 1.0,   isBlock: false },
            { itemId: ItemId.FEATHER,       min: 0, max: 2, chance: 1.0,   isBlock: false }],
  WOLF:    [], // Tamed pet, no drops

  // --- Hostile mobs ---
  ZOMBIE:   [{ itemId: ItemId.ROTTEN_FLESH, min: 0, max: 2, chance: 1.0,   isBlock: false },
             { itemId: ItemId.IRON_INGOT,    min: 1, max: 1, chance: 0.025, isBlock: false }],  // rare drop
  SKELETON: [{ itemId: ItemId.BONE,          min: 0, max: 2, chance: 1.0,   isBlock: false },
             { itemId: ItemId.ARROW_ITEM,    min: 0, max: 2, chance: 1.0,   isBlock: false }],
  CREEPER:  [{ itemId: ItemId.GUNPOWDER,    min: 0, max: 2, chance: 1.0,   isBlock: false }],
  SPIDER:   [{ itemId: ItemId.STRING,        min: 0, max: 2, chance: 1.0,   isBlock: false },
             { itemId: ItemId.SPIDER_EYE,    min: 1, max: 1, chance: 0.33,  isBlock: false }],
  ENDERMAN: [{ itemId: ItemId.ENDER_PEARL,  min: 0, max: 1, chance: 0.5,   isBlock: false }],
  SLIME:    [{ itemId: ItemId.SLIME_BALL,   min: 0, max: 2, chance: 1.0,   isBlock: false }],

  // --- Nether mobs ---
  ZOMBIE_PIGLIN: [{ itemId: ItemId.GOLD_INGOT,    min: 0, max: 1, chance: 0.5,  isBlock: false },
                  { itemId: ItemId.ROTTEN_FLESH,   min: 1, max: 1, chance: 1.0,  isBlock: false }],
  GHAST:   [{ itemId: ItemId.GHAST_TEAR,   min: 0, max: 1, chance: 0.5,   isBlock: false },
            { itemId: ItemId.GUNPOWDER,     min: 0, max: 2, chance: 1.0,   isBlock: false }],
  BLAZE:   [{ itemId: ItemId.BLAZE_ROD,    min: 0, max: 1, chance: 0.5,   isBlock: false }],

  // --- End mobs ---
  SHULKER: [{ itemId: ItemId.SHULKER_SHELL, min: 0, max: 2, chance: 0.5,  isBlock: false }],
  ENDERMITE: [], // no drops

  // --- Fase 7 mobs ---
  WARDEN: [], // effectively unkillable — no drops by design
  WITCH: [{ itemId: ItemId.SPIDER_EYE, min: 0, max: 2, chance: 0.5, isBlock: false },
          { itemId: ItemId.GUNPOWDER, min: 0, max: 2, chance: 0.5, isBlock: false }],
  PHANTOM: [{ itemId: ItemId.LEATHER, min: 0, max: 1, chance: 0.5, isBlock: false }],
  DROWNED: [{ itemId: ItemId.ROTTEN_FLESH, min: 0, max: 2, chance: 1.0, isBlock: false }],
  PILLAGER: [{ itemId: ItemId.ARROW_ITEM, min: 0, max: 2, chance: 0.5, isBlock: false }],
};

/**
 * Get the raw drop table for a mob type.
 *
 * @param {string} mobType - e.g. 'ZOMBIE', 'SKELETON', 'PIG', 'COW', etc.
 * @returns {Array<{ itemId?: number, blockId?: number, min: number, max: number, chance: number, isBlock: boolean }>}
 */
export function getMobDrop(mobType) {
  return MOB_DROPS[mobType] || [];
}

/**
 * Roll the actual drops from a mob's loot table.
 * Resolves chance rolls and random counts.
 *
 * @param {string} mobType
 * @param {number} [lootingLevel=0] - Looting enchantment level (0-3).
 *        Each level adds +1 to the max drop count for every entry.
 * @returns {Array<{ itemId: number, count: number, isBlock: boolean }>}
 */
export function rollMobDrops(mobType, lootingLevel = 0) {
  const table = getMobDrop(mobType);
  const results = [];

  for (const entry of table) {
    // Chance roll
    if (Math.random() >= entry.chance) continue;

    // Looting increases max drop count by +1 per level
    const effectiveMax = entry.max + Math.max(0, Math.floor(lootingLevel));
    const count = entry.min + Math.floor(Math.random() * (effectiveMax - entry.min + 1));
    if (count <= 0) continue;

    // Resolve the actual item id (blockId for block drops, itemId for items)
    const id = entry.blockId !== undefined ? entry.blockId : entry.itemId;

    results.push({
      itemId: id,
      count,
      isBlock: !!entry.isBlock,
    });
  }

  return results;
}

// ============================================================
// MOB XP VALUES
// ============================================================

/**
 * Experience points awarded when a mob is killed.
 * Keyed by mob type string.
 */
export const MOB_XP = {
  // Passive mobs
  PIG: 1, COW: 1, SHEEP: 1, CHICKEN: 1, WOLF: 1,
  // Hostile mobs
  ZOMBIE: 5, SKELETON: 5, CREEPER: 5, SPIDER: 5,
  ENDERMAN: 5, SLIME: 1,
  // Nether mobs
  ZOMBIE_PIGLIN: 5, GHAST: 5, BLAZE: 10,
  // End mobs
  SHULKER: 5, ENDERMITE: 3,
  // Fase 5-7 mobs
  WITCH: 5, PHANTOM: 5, DROWNED: 5, PILLAGER: 5, WARDEN: 100,
  HORSE: 1, CAT: 1, BEE: 1, IRON_GOLEM: 0, SNOW_GOLEM: 0,
  // Non-combat / special
  VILLAGER: 0, STEVE: 0, ALEX: 0, HEROBRINE: 0,
};
